import { featureFlags } from '@/featureFlags';
import { useAiAssistantAvailable } from '@/features/ai-assistant/hooks/useAiAssistantAvailable';
import { useMenuHealth } from '@/features/menu/hooks/useMenuHealth';
import { usePendingOrdersCount } from '@/features/orders/hooks/useOrders';
import { usePendingReservations } from '@/features/reservations/hooks/useReservations';
import { useUnreadCount } from '@/features/notifications/hooks/useInbox';
import { useAiChatStore } from '@/features/ai-assistant/stores/ai-chat.store';
import { LOCALES, useI18nStore, useT } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useUiStore } from '@/stores/ui.store';
import { navigateMain } from '@/utils/navigateMain';
import { confirmDiscardChanges } from '@/utils/unsavedChanges';
import { f7 } from 'framework7-react';
import { useEffect, useRef, useState } from 'react';
import { GB, VN, DE, PL } from 'country-flag-icons/react/3x2';

export const FLAGS: Record<'GB' | 'VN' | 'DE' | 'PL', typeof GB> = { GB, VN, DE, PL };

export interface NavItem {
  title: string;
  icon: string;
  /** Resting icon tint - a distinct accent per destination so the rail reads as
   * a coloured index rather than a wall of grey. The active item still switches
   * to the theme colour via CSS. */
  color: string;
  /** Section a tap navigates to. Omitted for pure action items (e.g. the AI
   * assistant, which opens an overlay rather than a routed page). */
  path?: string;
  /** Overrides plain navigation - runs instead of routing to `path`. */
  action?: () => void;
  badge?: number;
  warning?: string;
  /** Route pattern prefix for active-state matching. Defaults to `path`. */
  activePrefix?: string;
}

/**
 * Shared navigation model for the desktop/tablet chrome. Both the iOS floating
 * glass `Sidebar` and the Material 3 `NavigationRail` are pure presentation over
 * this one source of truth - the item lists, badge counts, active-route matching,
 * section navigation, language menu, and logout all live here so the two skins
 * can never drift apart.
 */
export function useNavModel() {
  const t = useT();
  const email = useAuthStore((s) => s.email);
  const logout = useAuthStore((s) => s.logout);
  const clearRestaurant = useRestaurantStore((s) => s.clear);
  const restaurantName = useRestaurantStore((s) => s.restaurantConfig?.name);
  // Hide the AI assistant nav entry entirely when it's not activated for this
  // restaurant (Restaurant.aiChatEnabled, managed in the partner portal).
  const aiAvailable = useAiAssistantAvailable();
  const openAssistant = useAiChatStore((s) => s.open);

  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const locale = useI18nStore((s) => s.locale);
  const setLocale = useI18nStore((s) => s.setLocale);
  const currentLang = LOCALES.find((l) => l.value === locale);

  const { data: pending } = usePendingReservations();
  const pendingReservationCount = Array.isArray(pending) ? pending.length : 0;
  const { data: pendingOrders } = usePendingOrdersCount();
  const pendingOrdersCount = pendingOrders ?? 0;
  const { data: unread } = useUnreadCount();
  const unreadCount = unread?.count ?? 0;
  const menuHealth = useMenuHealth();

  const [activePath, setActivePath] = useState('/');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!langOpen) return;
    const onDown = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLangOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  useEffect(() => {
    if (!userOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setUserOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userOpen]);

  useEffect(() => {
    const update = () => {
      const currentPath =
        f7.views.main?.router?.currentRoute?.url ?? (window.location.hash.slice(1) || '/');
      setActivePath(currentPath || '/');
    };
    update();
    const handler = () => update();
    window.addEventListener('popstate', handler);

    // Explicit destination broadcast from navigateMain() - the reliable signal
    // for programmatic top-level nav (e.g. Settings "Done"), since reloadAll
    // navigation doesn't dependably fire routeChanged on the listened router.
    const onMainNav = (e: Event) => {
      const path = (e as CustomEvent<string>).detail;
      if (path) setActivePath(path);
    };
    window.addEventListener('main-navigate', onMainNav);

    // `routeChanged` covers programmatic F7 navigation (router.navigate via
    // pushState, which does NOT fire popstate) - e.g. the Settings "Done" calling
    // navigateMain('/reservation'). The main <View> is a sibling that may not be
    // initialised when this mounts, so f7.views.main?.router can be undefined
    // here - retry until it exists, then subscribe. Without this the listener
    // silently never attaches and the highlight sticks on the prev item.
    let detach: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    const attach = () => {
      const router = f7.views.main?.router;
      if (router) {
        router.on('routeChanged', update);
        detach = () => router.off('routeChanged', update);
        update();
      } else if (tries++ < 40) {
        timer = setTimeout(attach, 50);
      }
    };
    attach();

    return () => {
      window.removeEventListener('popstate', handler);
      window.removeEventListener('main-navigate', onMainNav);
      if (timer) clearTimeout(timer);
      detach?.();
    };
  }, []);

  const navigate = (path: string) => {
    // Settings is a full-screen overlay on desktop, not a section in the main
    // view - so don't move the section highlight onto it, or it'd stay stuck on
    // "Settings" after the overlay closes (the underlying section never changed).
    const isSettings = path === '/settings' || path.startsWith('/settings/');
    if (!isSettings) setActivePath(path);
    navigateMain(path);
  };

  // Score how well an item's prefix matches the current path. The single active
  // item is the highest scorer (longest matching prefix) - so e.g. /menu
  // highlights "Menu", not the broader "Settings". -1 means no match.
  const matchScore = (item: NavItem): number => {
    const prefix = item.activePrefix ?? item.path;
    if (!prefix) return -1;
    if (prefix === '/') return activePath === '/' || activePath === '' ? 1 : -1;
    if (
      activePath === item.path ||
      activePath === prefix ||
      activePath.startsWith(prefix + '/') ||
      activePath.startsWith(prefix + '?')
    ) {
      return prefix.length;
    }
    return -1;
  };

  // Operations - the live-shift surfaces an operator works during service, plus
  // the fast-access tools (action centre, AI assistant) that support them.
  const operationsItems: NavItem[] = [
    {
      title: t.nav.reservations,
      icon: 'calendar_fill',
      color: '#0a84ff',
      path: '/reservation',
      badge: pendingReservationCount,
      activePrefix: '/reservation',
    },
    {
      title: t.nav.orders,
      icon: 'bag_fill',
      color: '#ff9500',
      path: '/orders',
      badge: pendingOrdersCount,
    },
    ...(featureFlags.tableOrdering
      ? [
          {
            title: t.nav.tables,
            icon: 'square_grid_2x2_fill',
            color: '#34c759',
            path: '/tables',
          },
        ]
      : []),
    ...(featureFlags.actionCenter
      ? [
          {
            title: t.nav.actionCenter,
            icon: 'bolt_fill',
            color: '#ffcc00',
            path: '/action-center',
          },
        ]
      : []),
    ...(aiAvailable
      ? [
          {
            title: t.nav.aiAssistant,
            icon: 'sparkles',
            color: '#bf5af2',
            action: () => openAssistant(),
          },
        ]
      : []),
  ];

  // Functionality - the management and catalogue surfaces set up outside service.
  const functionalityItems: NavItem[] = [
    {
      title: t.nav.customers,
      icon: 'person_2_fill',
      color: '#5e5ce6',
      path: '/customers',
    },
    {
      title: t.nav.menu,
      icon: 'square_list_fill',
      color: '#ff375f',
      path: '/menu',
      warning: menuHealth ? t.settingsHealth[menuHealth].title : undefined,
    },
    { title: t.nav.discount, icon: 'tag_fill', color: '#af52de', path: '/promotions' },
    ...(featureFlags.reports
      ? [{ title: t.nav.reports, icon: 'chart_bar_fill', color: '#30b0c7', path: '/reports' }]
      : []),
    ...(featureFlags.customerService
      ? [
          {
            title: t.nav.customerService,
            icon: 'bubble_left_bubble_right_fill',
            color: '#32ade6',
            path: '/customer-service',
          },
        ]
      : []),
    {
      title: t.nav.settings,
      icon: 'gear_alt_fill',
      color: '#8e8e93',
      path: '/settings',
      badge: featureFlags.inbox ? unreadCount : 0,
    },
  ];

  // Resolve the single active item up-front so only the best match highlights.
  const activeItemPath = [...operationsItems, ...functionalityItems].reduce<{
    path: string;
    score: number;
  }>(
    (best, item) => {
      const score = matchScore(item);
      return score > best.score ? { path: item.path ?? '', score } : best;
    },
    { path: '', score: -1 },
  ).path;

  const getInitials = (name: string) =>
    name
      .split(/[ @.]/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const handleLogout = () => {
    // Guard unsaved settings edits before signing out (which navigates away).
    void confirmDiscardChanges().then((proceed) => {
      if (!proceed) return;
      f7.dialog.confirm(t.signOutConfirm, t.signOut, () => {
        logout();
        clearRestaurant();
        f7.views.main.router.navigate('/login', { reloadAll: true });
      });
    });
  };

  const selectLocale = (value: typeof locale) => {
    setLocale(value);
    setLangOpen(false);
  };

  return {
    t,
    email,
    restaurantName,
    collapsed,
    toggleSidebar,
    locale,
    currentLang,
    langOpen,
    setLangOpen,
    langRef,
    userOpen,
    setUserOpen,
    userRef,
    operationsItems,
    functionalityItems,
    activeItemPath,
    navigate,
    handleLogout,
    selectLocale,
    getInitials,
  };
}
